import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../redux-store";
import { useForm } from "react-hook-form";
import { WaypointData } from "../../ducks/app/route-info/stops/types";
import { yupResolver } from "@hookform/resolvers/yup";
import { addWaypoint } from "../../ducks/routes";
import { AddressChange } from "../../Components/NewPlaceSearch";
import Grid from "@mui/material/Grid";
import PlaceSearchWithSavedLocations from "../../Components/PlaceSearchWithSavedLocations/PlaceSearchWithSavedLocations";
import { TextField } from "@mui/material";
import createEditWaypointValidationSchema from "../../Components/dispatching-planning/route-planning/createEditWaypointValidationSchema";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { DoraButton } from "dora-shared/frontend";

const LiteVersionCreateWaypointForm = ({ routeId }: { routeId: string }) => {
  const { t } = useTranslation(["dispatching", "modals"]);

  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState,
    getValues,
    setValue,
    watch,
    reset,
  } = useForm<WaypointData>({
    resolver: yupResolver(createEditWaypointValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      place: {
        placeName: "",
        address: "",
        city: "",
        coord: { lon: 0, lat: 0 },
        country: "",
        googlePlaceId: "",
      },
      note: "",
      shouldShowToDriver: true,
    },
  });
  const { isValid, isDirty, isSubmitting } = formState;

  const onSubmit = async () => {
    await dispatch(addWaypoint({ routeId, waypoint: getValues() }))
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(() => {
        // The addWaypoint thunk has already reported it and shown the error toast.
      });
  };

  const onPlaceSelected = (place: null | AddressChange) => {
    setValue(
      "place",
      {
        placeName: (place && place.placeName) || "",
        address: (place && place.address) || "",
        city: (place && place.city) || "",
        coord: place && place.coord,
        country: (place && place.country) || "",
        postcode: (place && place.postcode) || "",
        googlePlaceId: place?.googlePlaceId,
      },
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const googlePlaceCoords = watch("place.coord.lon");

  return (
    <div>
      <Grid container spacing={0} columnSpacing={1}>
        <Grid size={12}>
          <div className="mt-2 mb-1">
            <PlaceSearchWithSavedLocations
              onPlaceSelected={onPlaceSelected}
              placeError={false}
            />
          </div>
        </Grid>
        {googlePlaceCoords ? (
          <>
            <Grid size={12}>
              <TextField
                className="bg-white"
                InputLabelProps={{ shrink: true }}
                disabled
                label={t("modals:addNewCargo.stepOne.inputFields.address")}
                {...register("place.address")}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                className="bg-white"
                InputLabelProps={{ shrink: true }}
                disabled
                label={t("modals:addNewCargo.stepOne.inputFields.country")}
                {...register("place.country")}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                className="bg-white"
                InputLabelProps={{ shrink: true }}
                disabled
                label={t("modals:addNewCargo.stepOne.inputFields.postcode")}
                {...register("place.postcode")}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                className="bg-white"
                InputLabelProps={{ shrink: true }}
                disabled
                label={t("modals:addNewCargo.stepOne.inputFields.city")}
                {...register("place.city")}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                className="bg-white"
                multiline
                rows={4}
                maxRows={12}
                {...register("note")}
                placeholder={t("waypointDialog.notesPlaceholder")}
              />
            </Grid>
          </>
        ) : null}
      </Grid>
      <DoraButton
        onClick={handleSubmit(onSubmit)}
        className={"flex gap-2 items-center mt-2"}
        variant={"primary-filled"}
        disabled={!isValid || !isDirty || isSubmitting}
      >
        <AddCircleOutlineOutlinedIcon />
        <div>{t("liteVersion.addWaypoint")}</div>
      </DoraButton>
    </div>
  );
};

export default LiteVersionCreateWaypointForm;
